import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import actions from '@src/redux/actions';

const SEEK_THRESHOLD_SECONDS = 0.5;
const TIME_DRIFT_THRESHOLD_SECONDS = 0.1;
// HTMLMediaElement.HAVE_FUTURE_DATA — enough buffered data to play forward at least one frame
const READY_FOR_PLAYBACK = 3;
// Wait this long before showing the loading spinner. If the buffer recovers
// within this window, the spinner is never shown — keeps fast seeks silent.
const BUFFERING_SHOW_DELAY_MS = 400;

/**
 * Keeps a video element in sync with a TTS audio element.
 *
 * Rules:
 *  - Mirror the playback intent (Redux isPlaying) on the video element.
 *  - Mirror seeks: when audioCurrentTime jumps, set video.currentTime to match.
 *  - When either side starts buffering, pause both. Loading UI is shown only
 *    if the stall lasts longer than BUFFERING_SHOW_DELAY_MS.
 *  - When both sides are buffered enough to play, resume both together
 *    (synced to audio time).
 */
const useVideoPlaybackSync = ({
  enabled = true,
  videoRef,
  audioRef,
  isPlaying,
  audioCurrentTime,
}) => {
  const dispatch = useDispatch();
  const isPlayingRef = useRef(isPlaying);
  const lastAudioTimeRef = useRef(audioCurrentTime);
  // Authoritative position on the VIDEO timeline. Equals the audio element's
  // own currentTime in the legacy single-audio flow, but under joined
  // multi-block playback the element resets to 0 each block while this prop
  // keeps the cumulative project time — so drift correction must use this,
  // not audio.currentTime.
  const audioTimelineRef = useRef(audioCurrentTime);
  const showBufferingTimerRef = useRef(null);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    audioTimelineRef.current = audioCurrentTime;
  }, [audioCurrentTime]);

  // Show loading is delayed; hide is immediate. So a quick stall that recovers
  // within BUFFERING_SHOW_DELAY_MS never flashes the spinner.
  const setBuffering = useCallback(
    (value) => {
      if (value) {
        if (showBufferingTimerRef.current) return;
        showBufferingTimerRef.current = setTimeout(() => {
          showBufferingTimerRef.current = null;
          dispatch(actions.project.setIsBuffering(true));
        }, BUFFERING_SHOW_DELAY_MS);
        return;
      }
      if (showBufferingTimerRef.current) {
        clearTimeout(showBufferingTimerRef.current);
        showBufferingTimerRef.current = null;
      }
      dispatch(actions.project.setIsBuffering(false));
    },
    [dispatch],
  );

  // 1. Mirror play / pause intent on the video element
  useEffect(() => {
    if (!enabled) return undefined;
    const video = videoRef?.current;
    if (!video) return undefined;

    if (isPlaying) {
      const p = video.play();
      if (p?.catch) p.catch(() => {});
    } else {
      video.pause();
      if (showBufferingTimerRef.current) {
        clearTimeout(showBufferingTimerRef.current);
        showBufferingTimerRef.current = null;
      }
      dispatch(actions.project.setIsBuffering(false));
    }
    return undefined;
  }, [enabled, videoRef, isPlaying, dispatch]);

  // 2. Mirror seeks: when the audio time jumps (delta > threshold), seek the video too
  useEffect(() => {
    if (!enabled) return;
    const video = videoRef?.current;
    if (!video) {
      lastAudioTimeRef.current = audioCurrentTime;
      return;
    }
    const delta = audioCurrentTime - lastAudioTimeRef.current;
    if (Math.abs(delta) > SEEK_THRESHOLD_SECONDS) {
      video.currentTime = audioCurrentTime;
    }
    lastAudioTimeRef.current = audioCurrentTime;
  }, [enabled, videoRef, audioCurrentTime]);

  // 3. Buffer coordination — pause both whenever either stalls; resume both once both are ready
  useEffect(() => {
    if (!enabled) return undefined;
    const video = videoRef?.current;
    const audio = audioRef?.current;
    if (!video || !audio) return undefined;

    const onWaiting = () => {
      if (!isPlayingRef.current) return;
      setBuffering(true);
      if (!video.paused) video.pause();
      if (!audio.paused) audio.pause();
    };

    const onReady = () => {
      if (!isPlayingRef.current) return;
      // Only act if at least one side is paused — otherwise it's a normal `playing`
      // tick during steady playback and there's nothing to do.
      if (!video.paused && !audio.paused) return;
      if (
        video.readyState < READY_FOR_PLAYBACK ||
        audio.readyState < READY_FOR_PLAYBACK
      ) {
        return;
      }
      // Use the audio timeline as the authoritative clock (it carries the
      // dubbed voice). audioTimelineRef maps to the video timeline in both the
      // legacy and joined-multi-block flows; audio.currentTime does not under
      // joined playback (it restarts each block).
      const audioTimelineTime = audioTimelineRef.current;
      if (
        Math.abs(video.currentTime - audioTimelineTime) >
        TIME_DRIFT_THRESHOLD_SECONDS
      ) {
        video.currentTime = audioTimelineTime;
      }
      lastAudioTimeRef.current = audioTimelineTime;
      if (video.paused) {
        const p = video.play();
        if (p?.catch) p.catch(() => {});
      }
      if (audio.paused) {
        const p = audio.play();
        if (p?.catch) p.catch(() => {});
      }
      setBuffering(false);
    };

    video.addEventListener('waiting', onWaiting);
    audio.addEventListener('waiting', onWaiting);
    video.addEventListener('canplay', onReady);
    audio.addEventListener('canplay', onReady);
    video.addEventListener('playing', onReady);
    audio.addEventListener('playing', onReady);

    return () => {
      video.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('waiting', onWaiting);
      video.removeEventListener('canplay', onReady);
      audio.removeEventListener('canplay', onReady);
      video.removeEventListener('playing', onReady);
      audio.removeEventListener('playing', onReady);
    };
  }, [enabled, videoRef, audioRef, setBuffering]);

  // Cleanup: pause video on unmount
  useEffect(
    () => () => {
      if (!enabled) return;
      const video = videoRef?.current;
      if (video) video.pause();
      if (showBufferingTimerRef.current) {
        clearTimeout(showBufferingTimerRef.current);
        showBufferingTimerRef.current = null;
      }
      dispatch(actions.project.setIsBuffering(false));
    },
    [enabled, videoRef, dispatch],
  );
};

export default useVideoPlaybackSync;
